import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck } from "lucide-react";
import { useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FeatureOff } from "../components/FeatureOff";
import { RoleGroup } from "../components/RequireRole";
import { TableScroll } from "../components/TableScroll";
import { fieldLabel, fileName, lifecycleLabel, pageLabel, percent, vendorStageLabel, words } from "../lib/words";
import { OUTCOME_LABELS } from "../components/MarketResults";
import { fieldWord } from "../components/modules/moduleFlow";
import { ErrorState, Loading } from "../components/States";
import {
  settings,
  type ReviewDecision,
  type ReviewItem,
  type ReviewList,
  type ReviewShown,
  type ReviewThreshold,
} from "../lib/api";
import { switchedOff } from "../lib/http";
import { roleBlock, useMe, type Me } from "../lib/me";
import { when } from "../lib/when";
import { decideReview, fetchReviews, REVIEW_STATES, REVIEWS_KEY, type ReviewState } from "./reviewFlow";
import "../theme/review.css";

export const SOURCE_LABELS: Record<string, string> = {
  uc1_extraction: "UC1 extraction",
  uc5_job: "UC5 job",
  module_run: "Module run",
};

export const REASON_LABELS: Record<string, string> = {
  low_confidence: "low confidence",
  conflict: "conflict",
  no_box: "not marked on the page",
  gate: "waiting at a step",
  needs_review: "needs a person",
};

export const REASON_TONE: Record<string, string> = {
  conflict: "fail",
  no_box: "review",
  low_confidence: "review",
  gate: "info",
  needs_review: "review",
};

export function basename(path: string): string {
  return fileName(path);
}

/** What approving this item accepts, in a line or two, so nobody approves a value unseen. */
export function Shown({ shown }: { shown: ReviewShown | null | undefined }) {
  if (!shown) return <span className="muted">nothing to show</span>;
  if (shown.gate === "confirm_documents") {
    const docs = shown.documents ?? [];
    return <span>{docs.length ? `reads ${docs.map(basename).join(", ")}` : "no document recommended"}</span>;
  }
  if (shown.gate && shown.fields) {
    return (
      <span>
        {(shown.reasons ?? []).join("; ")}
        {shown.fields.map((f) => (
          <span key={f.name ?? ""}><br />{f.name ? fieldLabel(f.name) : ""}: <FieldReading shown={f} /></span>
        ))}
      </span>
    );
  }
  if (shown.gate) {
    const spec = Object.entries(shown.spec ?? {}).map(([k, v]) => `${fieldLabel(k)}: ${String(v)}`);
    const lines = [...spec, ...(shown.reasons ?? []), ...(shown.unresolved ?? []).map((u) => `${fieldLabel(u)} still disputed`)];
    return <span>{shown.tag ? `${shown.tag}: ` : ""}{lines.join("; ") || words(shown.gate)}</span>;
  }
  if (shown.stage) {
    const top = shown.options?.[0];
    const quote = top?.quote ?? shown.quote;
    const url = top?.source_url ?? shown.source_url;
    return (
      <span>
        {vendorStageLabel(shown.stage)}: {shown.status ? lifecycleLabel(shown.status) : "no result yet"}
        {top && ` (${shown.options?.length} option${shown.options?.length === 1 ? "" : "s"}, first ${top.outcome ? OUTCOME_LABELS[top.outcome] ?? words(top.outcome) : ""} ${top.part_number ?? ""})`}
        {quote && <><br /><q>{quote}</q></>}
        {url && <><br /><a href={url} target="_blank" rel="noreferrer">{url}</a></>}
      </span>
    );
  }
  if (shown.needs_review) {
    return (
      <span>
        Check: {shown.needs_review.map(fieldWord).join(", ")}
        {(shown.fields ?? []).map((f) => (
          <span key={f.name ?? ""}><br />{fieldLabel(f.name)}: <FieldReading shown={f} /></span>
        ))}
      </span>
    );
  }
  if (shown.finding) {
    const quote = shown.finding.evidence?.quote;
    return (
      <span>
        Lifecycle finding: {shown.finding.status ? lifecycleLabel(shown.finding.status) : "not known"}{shown.finding.disagreement ? ", and its sources disagree" : ""}
        {quote && <><br /><q>{quote}</q></>}
      </span>
    );
  }
  return <FieldReading shown={shown} />;
}

function FieldReading({ shown }: { shown: ReviewShown }) {
  const where = shown.document ? `${basename(shown.document)}, ${pageLabel(shown.page)}` : "no source";
  const others = (shown.alternatives ?? []).filter((a): a is string => !!a && a !== shown.value);
  return (
    <span>
      <span className="mono">{shown.value ?? "(empty)"}</span>
      {shown.confidence != null && ` at ${percent(shown.confidence)}`}
      {others.length > 0 && ` vs ${others.join(", ")}`}
      {" "}<span className="muted">({where})</span>
    </span>
  );
}

export function age(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

export { when };

const DECISION_TONE: Record<string, string> = { approved: "ok", rejected: "fail", returned: "review" };

const VERB: Record<ReviewDecision, string> = { approve: "approve", reject: "reject", return: "return" };

/** The four states as tiles, each with the API's count. The one shown is pressed. */
export function StateFilter({ state, counts, onChange }: {
  state: ReviewState; counts: Record<string, number>; onChange: (s: ReviewState) => void;
}) {
  return (
    <div className="tiles" style={{ marginBottom: 28 }} role="group" aria-label="Show items by state">
      {REVIEW_STATES.map((s) => (
        <button key={s} type="button" data-state={s} aria-pressed={s === state}
                className={`card tile tile-button ${s === "pending" && (counts.pending ?? 0) > 0 ? "highlight" : ""}`}
                onClick={() => onChange(s)}>
          <div className="l">{s}</div>
          <div className="n" data-count={s}>{counts[s] ?? 0}</div>
        </button>
      ))}
    </div>
  );
}

/**
 * Approve, reject or return. For anyone below an engineer the buttons stay visible, disabled,
 * with one reason beside them. Approve needs something on screen to approve.
 */
export function DecisionButtons({ me, item, busy, onPick }: {
  me: Me | null | undefined; item: ReviewItem; busy: boolean; onPick: (d: ReviewDecision) => void;
}) {
  const locked = busy || !!roleBlock(me, "engineer");
  return (
    <span className="role-gated">
      <RoleGroup role="engineer" me={me} label="Decision">
        <span className="row" style={{ gap: 6 }}>
          <button disabled={locked || !item.shown} onClick={() => onPick("approve")}>Approve</button>
          <button className="ghost" disabled={locked} onClick={() => onPick("reject")}>Reject</button>
          <button className="ghost" disabled={locked} onClick={() => onPick("return")}>Return</button>
        </span>
      </RoleGroup>
    </span>
  );
}

/** The note that goes with a decision, then the decision itself. */
export function DecisionForm({ decision, busy, onSubmit, onCancel }: {
  decision: ReviewDecision; busy: boolean; onSubmit: (note: string) => void; onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const id = useId();
  return (
    <form className="decision-form" onSubmit={(e) => { e.preventDefault(); onSubmit(note); }}>
      <label htmlFor={id}>Note {decision === "approve" ? "(optional)" : "(say why)"}</label>
      <textarea id={id} className="note" value={note} maxLength={2000} disabled={busy}
                onChange={(e) => setNote(e.target.value)}
                placeholder={decision === "approve" ? "Anything the next person should know" : "What is wrong, or what is still needed"} />
      <span className="hint">Recorded with your sign-in and the time. A decision cannot be changed once sent.</span>
      <span className="row" style={{ gap: 8 }}>
        <button type="submit" disabled={busy}>{busy ? "Saving..." : `Confirm ${VERB[decision]}`}</button>
        <button type="button" className="ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </span>
    </form>
  );
}

/** Who decided, when, and what they wrote. */
export function DecidedLine({ item }: { item: ReviewItem }) {
  if (!item.decision) return null;
  const d = item.decision;
  return (
    <span className="decided-line">
      <span className={`pill ${DECISION_TONE[d.decision] ?? "info"}`}>{d.decision}</span>{" "}
      by <span className="mono">{d.decided_by}</span> on {when(d.decided_at)}
      {d.note && <span className="note-text">&ldquo;{d.note}&rdquo;</span>}
    </span>
  );
}

/** One state of the queue as the API returned it. Counts are the API's, never recounted here. */
export function ReviewQueue({ list, state, me, busy, error, onDecide }: {
  list: ReviewList;
  state: ReviewState;
  me: Me | null | undefined;
  /** The id of the item a decision is being sent for. */
  busy: string | null;
  error: string | null;
  /** Resolves when the decision is recorded; rejects with the server's reason. */
  onDecide: (item: ReviewItem, decision: ReviewDecision, note: string) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState<{ id: string; decision: ReviewDecision } | null>(null);
  const shown = list.items.filter((i) => i.state === state);
  const pending = state === "pending";
  const cols = pending ? 8 : 7;

  async function send(item: ReviewItem, decision: ReviewDecision, note: string) {
    try {
      await onDecide(item, decision, note);
      setDraft(null);
    } catch {
      // The page shows the reason above the table and the note stays where it was typed.
    }
  }

  return (
    <>
      {error && <p role="alert" style={{ color: "var(--color-fail)", marginBottom: 12 }}>{error}</p>}

      <TableScroll label="Review queue" className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Source</th><th>Tag</th><th>Package</th><th>Field</th>
              <th>{pending ? "Under review" : "What was shown"}</th><th>Reason</th>
              {pending ? <><th>Age</th><th><span className="visually-hidden">Actions</span></th></> : <th>Decision</th>}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={cols} className="muted">
                {pending ? "Nothing is waiting for review." : `Nothing has been ${state} yet.`}
              </td></tr>
            )}
            {shown.map((i) => (
              <ReviewRow key={i.id} item={i} pending={pending} cols={cols} me={me}
                         busy={busy} draft={draft?.id === i.id ? draft.decision : null}
                         onPick={(decision) => setDraft({ id: i.id, decision })}
                         onCancel={() => setDraft(null)}
                         onSubmit={(decision, note) => void send(i, decision, note)} />
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function ReviewRow({ item: i, pending, cols, me, busy, draft, onPick, onCancel, onSubmit }: {
  item: ReviewItem; pending: boolean; cols: number; me: Me | null | undefined; busy: string | null;
  draft: ReviewDecision | null; onPick: (d: ReviewDecision) => void; onCancel: () => void;
  onSubmit: (d: ReviewDecision, note: string) => void;
}) {
  return (
    <>
      <tr data-review-row={i.id}>
        <td>{SOURCE_LABELS[i.source_type] ?? i.source_type}</td>
        <td className="mono">
          <Link to={`/review/${i.id}`} style={{ color: "var(--color-primary)" }}>{i.tag ?? "open"}</Link>
        </td>
        <td>{i.package ?? "-"}</td>
        <td>{i.field ? fieldLabel(i.field) : "-"}</td>
        <td><Shown shown={i.shown} /></td>
        <td><span className={`pill ${REASON_TONE[i.reason] ?? "info"}`}>{REASON_LABELS[i.reason] ?? i.reason}</span></td>
        {pending ? (
          <>
            <td className="mono">{age(i.age_seconds)}</td>
            <td><DecisionButtons me={me} item={i} busy={busy !== null} onPick={onPick} /></td>
          </>
        ) : (
          <td><DecidedLine item={i} /></td>
        )}
      </tr>
      {pending && draft && (
        <tr>
          <td colSpan={cols}>
            <DecisionForm decision={draft} busy={busy === i.id}
                          onSubmit={(note) => onSubmit(draft, note)} onCancel={onCancel} />
          </td>
        </tr>
      )}
    </>
  );
}

/** The cut fields are flagged against, as the API reports it. Read only on this page. */
export function ReviewThresholdNote({ threshold }: { threshold: ReviewThreshold }) {
  const percent = `${Math.round(threshold.value * 100)}%`;
  return (
    <p className="muted" data-review-threshold={threshold.value} style={{ marginBottom: 24 }}>
      Fields below <span className="mono">{percent}</span> confidence are flagged for review
      {threshold.editable
        ? threshold.changed_by
          ? <>, set by {threshold.changed_by}.</>
          : "."
        : <>, fixed until confidence is calibrated.</>}
    </p>
  );
}

/** Every flagged value, UC5 job and gated run, each somebody's job until it is decided. */
export function Review() {
  const { me } = useMe();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const asked = params.get("state");
  const state: ReviewState = REVIEW_STATES.includes(asked as ReviewState) ? (asked as ReviewState) : "pending";
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: [...REVIEWS_KEY, state],
    queryFn: () => fetchReviews(state),
    refetchInterval: 8000,
  });
  const threshold = useQuery({
    queryKey: ["review-threshold"], queryFn: settings.reviewThreshold, retry: false, staleTime: 60_000,
  });

  async function decide(item: ReviewItem, decision: ReviewDecision, note: string) {
    setBusy(item.id); setError(null);
    try {
      await decideReview(item.id, decision, note);
    } catch (e) {
      setError((e as Error).message);
      throw e;
    } finally {
      setBusy(null);
      void client.invalidateQueries({ queryKey: REVIEWS_KEY });
    }
  }

  const off = switchedOff(queue.error, [404]);
  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>
        <span className="row"><ClipboardCheck size={24} /> Review</span>
      </h2>
      <p className="muted" style={{ marginBottom: threshold.data ? 6 : 24 }}>
        Flagged fields, UC5 jobs and module runs waiting on a decision. Your decision is recorded under your sign-in.
      </p>
      {threshold.data && <ReviewThresholdNote threshold={threshold.data} />}
      {off !== null ? (
        <FeatureOff feature="The review queue" reason={off} />
      ) : queue.isPending ? (
        <Loading what="Loading the review queue" />
      ) : queue.error ? (
        <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
      ) : (
        <>
          <StateFilter state={state} counts={queue.data.counts}
                       onChange={(s) => setParams(s === "pending" ? {} : { state: s })} />
          <ReviewQueue list={queue.data} state={state} me={me} busy={busy} error={error} onDecide={decide} />
        </>
      )}
    </div>
  );
}
